import { motion } from "framer-motion";
import styled, { css } from "styled-components";
import {
  BACKGROUND_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { BORDER_RADIUS, SPACING } from "../../constants/design/spacing";
import { FONT_SIZE } from "../../constants/design/fontSize";

// --- Card Preview Wrapper ---

export const CardPreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${SPACING.three}px ${SPACING.three}px ${SPACING.two}px;
  background: ${BACKGROUND_COLORS.component};
  overflow-x: auto;
`;

// --- PnL Card (preview + export target) ---

export interface ICardTheme {
  bgMode: "dark" | "chart" | "gradient";
  orientation: "landscape" | "portrait";
  isPositive: boolean;
}

const CARD_LANDSCAPE = { width: 320, height: 200 };
const CARD_PORTRAIT = { width: 220, height: 310 };

export const PnLShareCard = styled.div<ICardTheme>`
  position: relative;
  overflow: hidden;
  border-radius: ${BORDER_RADIUS.five}px;
  background: ${BACKGROUND_COLORS.one};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ orientation }) =>
    orientation === "landscape"
      ? css`
          width: ${CARD_LANDSCAPE.width}px;
          height: ${CARD_LANDSCAPE.height}px;
        `
      : css`
          width: ${CARD_PORTRAIT.width}px;
          height: ${CARD_PORTRAIT.height}px;
        `}

  ${({ bgMode, isPositive }) =>
    bgMode === "gradient" &&
    css`
      background: ${isPositive
        ? "linear-gradient(135deg, #050d05 0%, #0a1f0a 60%, #0f2b10 100%)"
        : "linear-gradient(135deg, #0d0509 0%, #1f0a0e 60%, #2b0f13 100%)"};
    `}
`;

export const CardChartBg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const CardContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${SPACING.twoHalf}px;
`;

export const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;

  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  > span {
    font-size: ${FONT_SIZE.two};
    font-weight: 600;
    color: ${TEXT_COLORS.one};
    flex: 1;
  }
`;

export const DirectionBadge = styled.span<{ isLong: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ isLong }) =>
    isLong ? COLORS.up.four : COLORS.down.four};
  color: ${({ isLong }) =>
    isLong ? COLORS.up.one : COLORS.down.one};
  white-space: nowrap;
`;

export const CardPriceRow = styled.div`
  display: flex;
  gap: ${SPACING.three}px;
  margin-top: ${SPACING.two}px;
`;

export const CardPriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  > span:first-child {
    font-size: 10px;
    color: ${TEXT_COLORS.two};
    font-feature-settings: "tnum";
  }

  > span:last-child {
    font-size: 13px;
    font-weight: 500;
    color: ${TEXT_COLORS.one};
    font-feature-settings: "tnum";
  }
`;

export const CardPnlText = styled(motion.div)<{
  color: string;
  orientation?: "landscape" | "portrait";
}>`
  font-size: ${({ orientation }) =>
    orientation === "portrait" ? "26px" : "36px"};
  font-weight: 700;
  color: ${({ color }) => color};
  line-height: 1;
  margin-top: auto;
  font-feature-settings: "tnum";
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const CardBranding = styled.div`
  font-size: 10px;
  color: ${TEXT_COLORS.three};
  margin-top: ${SPACING.one}px;
  display: flex;
  align-items: center;
  gap: 4px;

  > span {
    font-weight: 600;
    color: ${TEXT_COLORS.two};
  }
`;
